import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Box, Button, Tab,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { HUMIDITY_TEMPERATURE_NAME } from '../../../../constants/graphs';
import { ReduxState } from '../../../../types';
import AlertModal from '../../AlertModal/AlertModal';
import AlertsTab from '../../AlertsTab/AlertsTab';
import { ALERT_LIST_UI } from '../../AlertsTab/constants';
import ConversationsTab from '../../ConversationTab';
import useStyles from '../../styles';
import { ModalState, EMPTY_MODAL_STATE } from '../../chartModal';
import useIsClientOperator from '../../../../hooks/useIsClientOperator';

const HumidityTemperatureConfigContent = ({
  sectorId,
  setModalState: setModalStateProp,
  refreshCounter = 0,
}: {
  sectorId: string;
  setModalState?: React.Dispatch<React.SetStateAction<ModalState>>;
  refreshCounter?: number;
}) => {
  const classes = useStyles();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);

  // Default to conversations tab when arriving from a notification (date param).
  const date = queryParams.get('date');
  const initialTabValue = date ? '3' : '1';

  const [tabValue, setTabValue] = useState(initialTabValue);
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [alertsRefreshCounter, setAlertsRefreshCounter] = useState(0);

  const [, setInternalModalState] = useState<ModalState>(EMPTY_MODAL_STATE);
  const setModalState = setModalStateProp ?? setInternalModalState;

  const isClientOperator = useIsClientOperator();

  React.useEffect(() => {
    if (isClientOperator && tabValue === '1') {
      setTabValue('2');
    }
  }, [isClientOperator, tabValue]);

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  }, []);

  return (
    <>
      <TabContext value={tabValue}>
        <Box sx={{
          borderBottom: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <TabList onChange={handleTabChange} aria-label="sector tabs">
            {!isClientOperator && (
              <Tab label="Configuración" value="1" className={classes.tabLabel} />
            )}
            <Tab label="Alertas" value="2" className={classes.tabLabel} />
            <Tab label="Conversaciones" value="3" className={classes.tabLabel} />
          </TabList>
          {tabValue === '2' && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setAlertModalOpen(true)}
              sx={{ ml: 2 }}
            >
              Crear Alerta
            </Button>
          )}
        </Box>
        {!isClientOperator && (
          <TabPanel value="1" className={classes.configTabPanel} />
        )}
        <TabPanel value="2" className={classes.configTabPanel}>
          <Box sx={{ position: 'relative', minHeight: '200px' }}>
            <AlertsTab
              sectorId={sectorId}
              headers={ALERT_LIST_UI}
              refreshCounter={alertsRefreshCounter}
              chartName={HUMIDITY_TEMPERATURE_NAME}
            />
          </Box>
        </TabPanel>
        <TabPanel value="3" className={classes.configTabPanel}>
          <ConversationsTab
            setModalState={setModalState}
            sectorId={sectorId}
            chartName={HUMIDITY_TEMPERATURE_NAME}
            refreshCounter={refreshCounter}
          />
        </TabPanel>
      </TabContext>
      <AlertModal
        interval={interval}
        open={alertModalOpen}
        onClose={() => setAlertModalOpen(false)}
        sectorId={sectorId}
        measurements={[
          { id: 'Temperature', name: 'Temperatura' },
          { id: 'Humidity', name: 'Humedad' },
        ]}
        chartName={HUMIDITY_TEMPERATURE_NAME}
        onAlertUpdated={() => setAlertsRefreshCounter((prev) => prev + 1)}
      />
    </>
  );
};

export default HumidityTemperatureConfigContent;
